import React, { useCallback, useState } from 'react';
import { Activity, Save, RefreshCw, Download, User, Scale, ChevronDown, ChevronUp, PlayCircle } from 'lucide-react';
import Button from '../../../../../components/ui/Button';

// Импорт секций формы
import PersonalParametersSection from './PersonalParametersSection';
import GoalSection from './GoalSection';
import DietPreferencesSection from './DietPreferencesSection';
import MedicalConditionsSection from './MedicalConditionsSection';

// Импорт утилитарных функций
import { isPositiveNumber, isInteger } from '../../../../../utils/validation';
import { calculateCalories } from '../../../../../utils/calculations/health';

/**
 * Компонент основной формы расчета калорий
 */
const CalculationForm = ({ 
  formData, 
  setFormData, 
  errors, 
  setErrors, 
  isCalculating, 
  setIsCalculating,
  results,
  setResults,
  setNutritionPlan,
  activityLevels,
  weightGoals,
  dietTypes,
  saveProfile 
}) => {
  
  // Состояние для имени сохраняемого профиля
  const [saveProfileName, setSaveProfileName] = useState('');
  const [showSaveDialog, setShowSaveDialog] = useState(false);
  const [showMedicalSection, setShowMedicalSection] = useState(false);
  
  // Функция парсинга числа с плавающей точкой или возврат значения по умолчанию
  const parseFloatOrDefault = (val, defaultVal = NaN) => {
    const parsed = parseFloat(String(val).replace(',', '.'));
    return isNaN(parsed) ? defaultVal : parsed;
  };
  
  // Функция парсинга целого числа или возврат значения по умолчанию
  const parseIntOrDefault = (val, defaultVal = NaN) => {
    const parsed = parseInt(String(val), 10);
    return isNaN(parsed) ? defaultVal : parsed;
  };
  
  // Обработчик изменения полей ввода
  const handleInputChange = useCallback((e) => {
    const { name, value, type, checked } = e.target;
    const fieldValue = type === 'checkbox' ? checked : value;

    setFormData(prev => {
      const newState = { ...prev, [name]: fieldValue };

      // Обновляем связанные поля если функция отключена
      if (name === 'considerMedicalConditions' && !checked) {
        newState.medicalConditions = [];
      }
      
      if (name === 'includeSnacks' && !checked) {
        newState.mealsPerDay = String(parseInt(prev.mealsPerDay, 10) - 
          (parseInt(prev.mealsPerDay, 10) > 3 ? (parseInt(prev.mealsPerDay, 10) - 3) : 0));
      }

      return newState;
    });

    // Очищаем ошибку для изменяемого поля
    if (errors[name]) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[name];
        return newErrors;
      });
    }
  }, [errors, setErrors, setFormData]);

  // Валидация формы
  const validateForm = useCallback(() => {
    const newErrors = {};
    const {
      gender, age, weight, height, activityLevel, goal,
      weightLossRate, dietType, mealsPerDay
    } = formData;

    // Парсинг числовых значений
    const numAge = parseIntOrDefault(age, -1);
    const numWeight = parseFloatOrDefault(weight, -1);
    const numHeight = parseFloatOrDefault(height, -1);
    const numWeightLossRate = parseFloatOrDefault(weightLossRate, -1);
    const numMealsPerDay = parseIntOrDefault(mealsPerDay, -1);

    // Валидация базовых полей
    if (!gender) newErrors.gender = 'Выберите пол.';
    
    if (numAge <= 0) newErrors.age = 'Возраст должен быть положительным числом.';
    if (!newErrors.age && numAge < 18) newErrors.age = 'Калькулятор предназначен для людей старше 18 лет.';
    if (!newErrors.age && numAge > 100) newErrors.age = 'Возраст должен быть не более 100 лет.';
    
    if (numWeight <= 0) newErrors.weight = 'Вес должен быть положительным числом.';
    if (!newErrors.weight && numWeight < 30) newErrors.weight = 'Вес должен быть не менее 30 кг.';
    if (!newErrors.weight && numWeight > 300) newErrors.weight = 'Вес должен быть не более 300 кг.';
    
    if (numHeight <= 0) newErrors.height = 'Рост должен быть положительным числом.';
    if (!newErrors.height && numHeight < 100) newErrors.height = 'Рост должен быть не менее 100 см.';
    if (!newErrors.height && numHeight > 250) newErrors.height = 'Рост должен быть не более 250 см.';
    
    if (!activityLevel) newErrors.activityLevel = 'Выберите уровень активности.';
    
    if (!goal) newErrors.goal = 'Выберите цель.';
    
    if (numWeightLossRate <= 0) newErrors.weightLossRate = 'Скорость снижения веса должна быть положительным числом.';
    if (!newErrors.weightLossRate && numWeightLossRate > 2) {
      newErrors.weightLossRate = 'Безопасная скорость снижения веса не более 2 кг в неделю.';
    }
    
    if (!dietType) newErrors.dietType = 'Выберите тип питания.';
    
    if (numMealsPerDay <= 0) newErrors.mealsPerDay = 'Количество приемов пищи должно быть положительным числом.';
    if (!newErrors.mealsPerDay && numMealsPerDay > 6) {
      newErrors.mealsPerDay = 'Количество приемов пищи должно быть не более 6.';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  }, [formData, setErrors]);

  // Функция создания плана питания
  const generateNutritionPlan = (calorieResults) => {
    if (!calorieResults) return null;
    
    const { dailyCalories, protein, fat, carbs } = calorieResults;
    const { mealsPerDay, includeSnacks, dietType } = formData;
    
    const numMeals = parseInt(mealsPerDay, 10);
    const selectedDiet = dietTypes.find(d => d.id === dietType) || dietTypes[0];
    
    // Создаем основные приемы пищи (завтрак, обед, ужин)
    const mainMeals = Math.min(3, numMeals);
    const snacks = numMeals - mainMeals;
    
    // Распределяем калории по приемам пищи
    let mealDist = [];
    
    switch(mainMeals) {
      case 1:
        mealDist = [1]; // 100% на один прием пищи
        break;
      case 2:
        mealDist = [0.4, 0.6]; // 40% завтрак, 60% ужин
        break;
      case 3:
      default:
        mealDist = [0.3, 0.4, 0.3]; // 30% завтрак, 40% обед, 30% ужин
        break;
    }
    
    // Если есть перекусы, перераспределяем калории
    if (snacks > 0) {
      // Отнимаем от основных приемов пищи по 5-10% на каждый перекус
      const snackPerc = Math.min(0.15, 0.3 / snacks); // от 5% до 10% на перекус
      const totalSnackPerc = snackPerc * snacks;
      
      // Корректируем основные приемы пищи
      for (let i = 0; i < mealDist.length; i++) {
        mealDist[i] = mealDist[i] * (1 - totalSnackPerc);
      }
      
      // Добавляем перекусы
      for (let i = 0; i < snacks; i++) {
        mealDist.push(snackPerc);
      }
    }
    
    // Создаем названия для приемов пищи
    const mealNames = ['Завтрак', 'Обед', 'Ужин'];
    for (let i = 0; i < snacks; i++) {
      mealNames.push(`Перекус ${i+1}`);
    }
    
    // Создаем план питания
    const plan = mealDist.map((percentage, idx) => {
      const mealCalories = Math.round(dailyCalories * percentage);
      const mealProtein = Math.round(protein * percentage);
      const mealFat = Math.round(fat * percentage);
      const mealCarbs = Math.round(carbs * percentage);
      
      return {
        name: mealNames[idx] || `Прием пищи ${idx+1}`,
        calories: mealCalories,
        protein: mealProtein,
        fat: mealFat,
        carbs: mealCarbs,
        percentage: percentage * 100,
        foods: generateSampleFoods(mealCalories, mealProtein, mealFat, mealCarbs, mealNames[idx])
      };
    });
    
    return plan;
  };
  
  // Функция генерации примерных продуктов для приема пищи
  const generateSampleFoods = (calories, protein, fat, carbs, mealType) => {
    // Упрощенные, примерные списки продуктов по типам приемов пищи
    const mealOptions = {
      'Завтрак': [
        { name: 'Овсяная каша на молоке', calories: 200, protein: 7, fat: 5, carbs: 30, portion: '200 г' },
        { name: 'Яичница из 2 яиц', calories: 180, protein: 12, fat: 14, carbs: 1, portion: '100 г' },
        { name: 'Творог 5%', calories: 110, protein: 18, fat: 5, carbs: 3, portion: '100 г' },
        { name: 'Цельнозерновой хлеб', calories: 80, protein: 3, fat: 1, carbs: 15, portion: '30 г' },
        { name: 'Банан', calories: 90, protein: 1, fat: 0, carbs: 23, portion: '1 шт.' },
      ],
      'Обед': [
        { name: 'Куриная грудка', calories: 165, protein: 31, fat: 3.6, carbs: 0, portion: '100 г' },
        { name: 'Гречневая каша', calories: 130, protein: 5, fat: 1, carbs: 25, portion: '100 г' },
        { name: 'Овощной салат с маслом', calories: 150, protein: 2, fat: 12, carbs: 10, portion: '150 г' },
        { name: 'Борщ', calories: 120, protein: 5, fat: 5, carbs: 12, portion: '250 мл' },
        { name: 'Рыба запеченная', calories: 160, protein: 30, fat: 5, carbs: 0, portion: '120 г' },
      ],
      'Ужин': [
        { name: 'Творожная запеканка', calories: 200, protein: 15, fat: 10, carbs: 15, portion: '150 г' },
        { name: 'Тушеные овощи', calories: 85, protein: 3, fat: 2, carbs: 15, portion: '150 г' },
        { name: 'Индейка запеченная', calories: 150, protein: 25, fat: 5, carbs: 0, portion: '100 г' },
        { name: 'Кефир 1%', calories: 40, protein: 3, fat: 1, carbs: 4, portion: '200 мл' },
        { name: 'Рыба на пару', calories: 130, protein: 24, fat: 4, carbs: 0, portion: '100 г' },
      ],
      'Перекус': [
        { name: 'Яблоко', calories: 80, protein: 0.4, fat: 0.3, carbs: 20, portion: '1 шт.' },
        { name: 'Йогурт натуральный', calories: 90, protein: 5, fat: 3, carbs: 9, portion: '125 г' },
        { name: 'Горсть орехов', calories: 170, protein: 6, fat: 15, carbs: 5, portion: '30 г' },
        { name: 'Творожок', calories: 130, protein: 10, fat: 5, carbs: 12, portion: '100 г' },
        { name: 'Протеиновый коктейль', calories: 130, protein: 25, fat: 1, carbs: 3, portion: '30 г' },
      ]
    };
    
    // Выбираем тип продуктов на основе приема пищи
    let options = mealOptions['Перекус'];
    if (mealType === 'Завтрак' || mealType === 'Обед' || mealType === 'Ужин') {
      options = mealOptions[mealType];
    }
    
    // Выбираем случайно 3-4 продукта
    const numProducts = Math.floor(Math.random() * 2) + 3; // 3 или 4 продукта
    const selectedOptions = [];
    
    // Копируем опции, чтобы не изменять исходный массив
    const availableOptions = [...options];
    
    // Масштабируем порции, чтобы приблизиться к целевым макросам
    const targetSum = {
      calories: calories,
      protein: protein,
      fat: fat,
      carbs: carbs
    };
    
    // Выбираем случайные продукты
    for (let i = 0; i < numProducts && availableOptions.length > 0; i++) {
      const randomIndex = Math.floor(Math.random() * availableOptions.length);
      const selected = { ...availableOptions[randomIndex] };
      
      // Удаляем выбранный продукт из доступных
      availableOptions.splice(randomIndex, 1);
      
      // Масштабируем порцию для приближения к целевым калориям
      const scaleFactor = Math.random() * 0.5 + 0.75; // от 0.75 до 1.25
      
      selected.calories = Math.round(selected.calories * scaleFactor);
      selected.protein = Math.round(selected.protein * scaleFactor);
      selected.fat = Math.round(selected.fat * scaleFactor);
      selected.carbs = Math.round(selected.carbs * scaleFactor);
      
      // Обновляем порцию с учетом масштабирования
      const portionValue = parseInt(selected.portion, 10);
      if (!isNaN(portionValue)) {
        const unit = selected.portion.replace(/[0-9]/g, '').trim();
        selected.portion = Math.round(portionValue * scaleFactor) + ' ' + unit;
      }
      
      selectedOptions.push(selected);
    }
    
    // Рассчитываем общие показатели
    const totalActual = {
      calories: selectedOptions.reduce((sum, food) => sum + food.calories, 0),
      protein: selectedOptions.reduce((sum, food) => sum + food.protein, 0),
      fat: selectedOptions.reduce((sum, food) => sum + food.fat, 0),
      carbs: selectedOptions.reduce((sum, food) => sum + food.carbs, 0)
    };
    
    return {
      foods: selectedOptions,
      total: totalActual,
      target: targetSum
    };
  };

  // Расчет калорий
  const calculateCalorieNeeds = useCallback(() => {
    if (!validateForm()) {
      // Опционально прокрутка к первой ошибке
      const firstErrorKey = Object.keys(errors)[0];
      if (firstErrorKey) {
        const errorElement = document.getElementById(firstErrorKey);
        errorElement?.focus();
        errorElement?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
      return;
    }

    setIsCalculating(true);
    setResults(null);
    setNutritionPlan(null);

    // Используем setTimeout для обновления UI перед расчетами
    setTimeout(() => {
      try {
        // --- Подготавливаем входные данные ---
        const {
          gender, age, weight, height, activityLevel, goal,
          weightLossRate, dietType
        } = formData;

        const numWeight = parseFloatOrDefault(weight);
        const numHeight = parseFloatOrDefault(height);
        const numAge = parseIntOrDefault(age);
        
        // Находим коэффициент активности
        const activity = activityLevels.find(a => a.id === activityLevel) || activityLevels[1];
        const activityFactor = activity.factor;
        
        // Находим цель и соответствующий дефицит/профицит калорий
        const selectedGoal = weightGoals.find(g => g.id === goal) || weightGoals[0];
        
        // Находим предпочитаемый тип диеты и распределение макронутриентов
        const selectedDiet = dietTypes.find(d => d.id === dietType) || dietTypes[0];
        
        // --- Расчеты ---
        
        // 1. Расчет базового метаболизма (BMR) по формуле Миффлина-Сан Жеора
        let bmr = 0;
        if (gender === 'male') {
          bmr = 10 * numWeight + 6.25 * numHeight - 5 * numAge + 5;
        } else {
          bmr = 10 * numWeight + 6.25 * numHeight - 5 * numAge - 161;
        }
        
        // 2. Расчет общих затрат энергии (TDEE) с учетом уровня активности
        const tdee = bmr * activityFactor;
        
        // 3. Расчет целевого потребления калорий с учетом цели по изменению веса
        let targetCalories = tdee;
        const weeklyCaloriesToLoseKilo = 7700; // примерно столько калорий в 1 кг жира
        
        // Применяем дефицит/профицит калорий
        if (selectedGoal.deficit) {
          const calorieDeficit = weeklyCaloriesToLoseKilo * selectedGoal.deficit / 7; // дневной дефицит
          targetCalories = tdee - calorieDeficit;
        } else if (selectedGoal.surplus) {
          const calorieSurplus = weeklyCaloriesToLoseKilo * selectedGoal.surplus / 7; // дневной профицит
          targetCalories = tdee + calorieSurplus;
        }
        
        // Убедимся, что целевые калории не ниже безопасного минимума
        const minSafeCalories = gender === 'male' ? 1500 : 1200;
        targetCalories = Math.max(targetCalories, minSafeCalories);
        
        // 4. Расчет макронутриентов
        // Белок: 1.5-2 г на кг веса для снижения веса, 1-1.5 г для поддержания
        let proteinPerKg = 1.2; // базовое значение
        
        if (goal.includes('loss')) {
          proteinPerKg = 1.8; // больше белка при снижении веса
        } else if (goal.includes('gain')) {
          proteinPerKg = 2.0; // еще больше при наборе мышечной массы
        }
        
        const proteinGrams = numWeight * proteinPerKg;
        const proteinCalories = proteinGrams * 4; // 4 ккал на грамм белка
        
        // Распределение оставшихся калорий согласно выбранному типу диеты
        const remainingCalories = targetCalories - proteinCalories;
        
        // Распределение по макронутриентам согласно типу диеты
        const macroPercentages = selectedDiet.macros;
        
        let fatGrams = 0;
        let carbGrams = 0;
        
        // Если выбран кето или низкоуглеводный подход, сначала считаем минимальное количество углеводов
        if (dietType === 'keto' || dietType === 'low_carb') {
          // Для кето - очень мало углеводов
          const minCarbGrams = dietType === 'keto' ? 30 : 100;
          const carbCalories = minCarbGrams * 4; // 4 ккал на грамм углеводов
          
          // Остальное - на жиры
          const fatCalories = remainingCalories + proteinCalories - carbCalories;
          fatGrams = fatCalories / 9; // 9 ккал на грамм жиров
          carbGrams = minCarbGrams;
        } else {
          // Для других диет используем проценты
          const carbCalories = remainingCalories * (macroPercentages.carbs / (macroPercentages.carbs + macroPercentages.fat));
          const fatCalories = remainingCalories - carbCalories;
          
          fatGrams = fatCalories / 9; // 9 ккал на грамм жиров
          carbGrams = carbCalories / 4; // 4 ккал на грамм углеводов
        }
        
        // 5. Прогнозирование снижения веса
        let weeklyWeightChange = 0;
        let timeToGoal = 0;
        let goalWeight = 0;
        
        if (selectedGoal.deficit) {
          weeklyWeightChange = -selectedGoal.deficit; // отрицательное значение для снижения веса
          
          // Определяем целевой вес
          // Используем индекс массы тела (ИМТ) для приблизительной оценки
          const heightInMeters = numHeight / 100;
          const currentBMI = numWeight / (heightInMeters * heightInMeters);
          const targetBMI = 22.5; // средний показатель нормального ИМТ
          
          // Учитываем текущий ИМТ - если он уже в норме, снижение веса может быть необязательным
          if (currentBMI > 25) {
            // Если ИМТ > 25 (избыточный вес), рассчитываем вес для достижения ИМТ = 23
            goalWeight = targetBMI * heightInMeters * heightInMeters;
            const weightToLose = numWeight - goalWeight;
            
            // Время до цели (недели)
            timeToGoal = weightToLose / selectedGoal.deficit;
          } else {
            // Если ИМТ в норме, допускаем снижение на 3-5% от текущего веса
            goalWeight = numWeight * 0.95; // 5% снижение
            const weightToLose = numWeight - goalWeight;
            
            // Время до цели (недели)
            timeToGoal = weightToLose / selectedGoal.deficit;
          }
        } else if (selectedGoal.surplus) {
          weeklyWeightChange = selectedGoal.surplus; // положительное значение для набора веса
          
          // Для набора веса аналогично рассчитываем целевые показатели
          goalWeight = numWeight * 1.1; // 10% увеличение от текущего веса
          const weightToGain = goalWeight - numWeight;
          
          // Время до цели (недели)
          timeToGoal = weightToGain / selectedGoal.surplus;
        }
        
        // Результаты расчетов
        const calculatedResults = {
          bmr: Math.round(bmr),
          tdee: Math.round(tdee),
          dailyCalories: Math.round(targetCalories),
          protein: Math.round(proteinGrams),
          fat: Math.round(fatGrams),
          carbs: Math.round(carbGrams),
          macroRatio: {
            protein: Math.round(proteinGrams * 4 / targetCalories * 100),
            fat: Math.round(fatGrams * 9 / targetCalories * 100),
            carbs: Math.round(carbGrams * 4 / targetCalories * 100)
          },
          weeklyWeightChange: weeklyWeightChange,
          timeToGoal: Math.round(timeToGoal),
          goalWeight: Math.round(goalWeight),
          activityFactorUsed: activityFactor,
          calorieDeficitSurplus: selectedGoal.deficit ? 
            -Math.round(weeklyCaloriesToLoseKilo * selectedGoal.deficit / 7) : 
            (selectedGoal.surplus ? Math.round(weeklyCaloriesToLoseKilo * selectedGoal.surplus / 7) : 0),
          dietType: selectedDiet.name
        };
        
        // Генерируем план питания
        const nutritionPlan = generateNutritionPlan(calculatedResults);
        
        setResults(calculatedResults);
        setNutritionPlan(nutritionPlan);
      } catch (error) {
        console.error("Ошибка расчета:", error);
        setErrors({ general: `Произошла ошибка при расчете: ${error.message}. Пожалуйста, проверьте введенные данные или попробуйте позже.` });
        setResults(null);
        setNutritionPlan(null);
      } finally {
        setIsCalculating(false);
      }
    }, 50);
  }, [formData, activityLevels, dietTypes, weightGoals, validateForm, errors, setErrors, setResults, setNutritionPlan, setIsCalculating]);

  // Сброс формы
  const resetForm = () => {
    setFormData({
      gender: 'female',
      age: '30',
      weight: '70',
      height: '165',
      activityLevel: 'low_active',
      goal: 'moderate_loss',
      weightLossRate: '0.5',
      excludeFoods: [],
      dietType: 'balanced',
      mealsPerDay: '3',
      includeSnacks: true,
      considerMedicalConditions: false,
      medicalConditions: [],
    });
    setResults(null);
    setNutritionPlan(null);
    setErrors({});
    setSaveProfileName('');
    setShowSaveDialog(false);
    setShowMedicalSection(false);
  };

  // Экспорт результатов в CSV
  const exportToCSV = () => {
    if (!results) {
      alert("Нет данных для экспорта. Сначала выполните расчет.");
      return;
    }

    // Данные для экспорта
    const headers = [
      'Параметр', 'Значение', 'Единица измерения'
    ];

    const rows = [
      ['Пол', formData.gender === 'male' ? 'Мужской' : 'Женский', ''],
      ['Возраст', formData.age, 'лет'],
      ['Вес', formData.weight, 'кг'],
      ['Рост', formData.height, 'см'],
      ['Уровень активности', activityLevels.find(a => a.id === formData.activityLevel)?.name || formData.activityLevel, ''],
      ['Цель', weightGoals.find(g => g.id === formData.goal)?.name || formData.goal, ''],
      ['Базовый метаболизм (BMR)', results.bmr, 'ккал/день'],
      ['Затраты энергии с учетом активности (TDEE)', results.tdee, 'ккал/день'],
      ['Целевое потребление калорий', results.dailyCalories, 'ккал/день'],
      ['Рекомендуемый белок', results.protein, 'г/день'],
      ['Рекомендуемые жиры', results.fat, 'г/день'],
      ['Рекомендуемые углеводы', results.carbs, 'г/день'],
      ['Еженедельное изменение веса', results.weeklyWeightChange, 'кг/неделю'],
      ['Примерное время до целевого веса', results.timeToGoal, 'недель'],
      ['Целевой вес', results.goalWeight, 'кг'],
      ['Дефицит/профицит калорий', results.calorieDeficitSurplus, 'ккал/день'],
      ['Тип диеты', results.dietType, '']
    ];

    // UTF-8 BOM для правильной кодировки в Excel
    const csvContent = '\uFEFF' + [headers.join(';'), ...rows.map(row => row.join(';'))].join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.setAttribute('href', url);
    link.setAttribute('download', 'расчет_калорий_для_похудения.csv');
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  // Обработчик сохранения профиля
  const handleSaveProfile = () => {
    if (showSaveDialog) {
      const savedResult = saveProfile(saveProfileName || null);
      if (savedResult) {
        alert(`Профиль "${savedResult.name}" успешно сохранен!`);
      }
      setShowSaveDialog(false);
      setSaveProfileName('');
    } else {
      setShowSaveDialog(true);
    }
  };

  // Обработчик отправки формы
  const handleSubmit = (e) => {
    e.preventDefault();
    calculateCalorieNeeds();
  };

  return (
    <form onSubmit={handleSubmit} noValidate>
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-md border border-gray-200 dark:border-gray-700 p-6 sm:p-8 mb-8">
        <h2 className="text-2xl font-semibold mb-6 text-gray-900 dark:text-white flex items-center gap-2">
          <Activity className="w-6 h-6 text-emerald-500" />
          Расчет индивидуальной потребности в калориях
        </h2>

        {/* Общая ошибка */}
        {errors.general && (
          <div role="alert" className="mb-4 p-3 bg-red-100 dark:bg-red-900/30 border border-red-300 dark:border-red-600 rounded-md text-red-700 dark:text-red-300 text-sm">
            {errors.general}
          </div>
        )}

        {/* Параметры человека */}
        <PersonalParametersSection 
          formData={formData} 
          handleInputChange={handleInputChange} 
          errors={errors} 
          activityLevels={activityLevels}
        />

        {/* Цели по снижению веса */}
        <GoalSection 
          formData={formData} 
          handleInputChange={handleInputChange} 
          errors={errors} 
          weightGoals={weightGoals}
        />

        {/* Предпочтения в питании */}
        <DietPreferencesSection 
          formData={formData} 
          handleInputChange={handleInputChange} 
          errors={errors} 
          dietTypes={dietTypes}
        />

        {/* Медицинские условия (сворачиваемая секция) */}
        <div className="mb-6">
          <button
            type="button"
            onClick={() => setShowMedicalSection(!showMedicalSection)}
            className="w-full flex justify-between items-center p-3 bg-gray-50 dark:bg-gray-700/30 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
          >
            <span className="font-medium text-gray-700 dark:text-gray-300 flex items-center">
              <User className="w-5 h-5 mr-2 text-emerald-500" />
              Учет медицинских особенностей
            </span>
            {showMedicalSection ? 
              <ChevronUp className="w-5 h-5 text-gray-500" /> : 
              <ChevronDown className="w-5 h-5 text-gray-500" />
            }
          </button>
          
          {showMedicalSection && (
            <div className="mt-3 p-4 bg-gray-50 dark:bg-gray-800/30 rounded-lg border border-gray-200 dark:border-gray-700">
              <MedicalConditionsSection 
                formData={formData} 
                handleInputChange={handleInputChange} 
                errors={errors}
              />
            </div>
          )}
        </div>

        {/* Диалог сохранения профиля */}
        {showSaveDialog && (
          <div className="mt-4 mb-6 p-4 bg-emerald-50 dark:bg-emerald-900/20 border border-emerald-100 dark:border-emerald-800 rounded-lg">
            <div className="flex items-center">
              <input
                type="text"
                placeholder="Введите название для сохранения профиля"
                value={saveProfileName}
                onChange={(e) => setSaveProfileName(e.target.value)}
                className="form-input flex-grow mr-3"
                autoFocus
              />
              <Button
                type="button"
                onClick={handleSaveProfile}
                primary
                size="sm"
              >
                Сохранить
              </Button>
            </div>
          </div>
        )}

        {/* Кнопки действий */}
        <div className="mt-8 flex flex-wrap gap-4">
          <Button
            type="submit"
            primary
            icon={<PlayCircle className="w-5 h-5" />}
            disabled={isCalculating || Object.keys(errors).length > 0}
            isLoading={isCalculating}
            size="lg"
          >
            {isCalculating ? 'Расчет...' : 'Рассчитать калории'}
          </Button>
          <Button
            type="button"
            onClick={resetForm}
            icon={<RefreshCw className="w-5 h-5" />}
            variant="secondary"
            disabled={isCalculating}
            size="lg"
          >
            Сбросить
          </Button>
          {results && (
            <Button
              type="button"
              onClick={exportToCSV}
              icon={<Download className="w-5 h-5" />}
              variant="secondary"
              disabled={isCalculating}
              size="lg"
            >
              Скачать результаты (CSV)
            </Button>
          )}
          {results && !showSaveDialog && (
            <Button
              type="button"
              onClick={handleSaveProfile}
              icon={<Save className="w-5 h-5" />}
              variant="secondary"
              disabled={isCalculating}
              size="lg"
            >
              Сохранить профиль
            </Button>
          )}
        </div>
        {Object.keys(errors).length > 0 && !errors.general && (
          <p className="mt-4 text-sm text-red-600 dark:text-red-400" role="alert">Пожалуйста, исправьте ошибки в форме.</p>
        )}
      </div>
    </form>
  );
};

export default CalculationForm;